

// Función auxiliar para encontrar una categoría por su ID
import {Category} from "@eshop/types/categories.types.ts";

export const findCategoryById = (categories: Category[], id: number): Category | undefined => {
    for (const category of categories) {
        if (category.id === id) {
            return category;
        }
        if (category.children) {
            const found = findCategoryById(category.children, id);
            if (found) return found;
        }
    }
    return undefined;
};

export const buildCategoryPath = (categories: Category[], id: number): string | null => {
    const traverse = (node: Category, acc: string[]): string[] | null => {
        const segment = node.parent_id ? `${node.slug}_${node.id}` : node.slug;
        const nextAcc = [...acc, segment];
        if (node.id === id) return nextAcc;
        if (node.children) {
            for (const child of node.children) {
                const res = traverse(child, nextAcc);
                if (res) return res;
            }
        }
        return null;
    };
    for (const root of categories) {
        const res = traverse(root, []);
        if (res) return res.join('/');
    }
    return null;
};

const sortCategoriesByName = (categories: Category[]): Category[] =>
    [...categories].sort((a, b) => a.name.localeCompare(b.name));

export const upsertCategoryNode = (
    categories: Category[],
    category: Category,
): Category[] => {
    const normalized: Category = {
        ...category,
        children: category.children ? [...category.children] : category.children ?? [],
    };

    const appendChild = (children: Category[]): Category[] => {
        const next = [...children];
        const existingIndex = next.findIndex((child) => child.id === normalized.id);
        if (existingIndex >= 0) {
            const existing = next[existingIndex];
            next[existingIndex] = {
                ...existing,
                ...normalized,
                children:
                    normalized.children && normalized.children.length
                        ? normalized.children
                        : existing.children ?? [],
            };
        } else {
            next.push({
                ...normalized,
                children: normalized.children ? [...normalized.children] : [],
            });
        }
        return sortCategoriesByName(next);
    };

    const visit = (nodes: Category[]): [Category[], boolean] => {
        let mutated = false;
        const mapped = nodes.map((node) => {
            if (node.id === normalized.id) {
                mutated = true;
                return {
                    ...node,
                    ...normalized,
                    children:
                        normalized.children && normalized.children.length
                            ? normalized.children
                            : node.children ?? [],
                };
            }

            let children = node.children ?? [];
            let childMutated = false;

            if (children.length) {
                const [updatedChildren, hasMutated] = visit(children);
                if (hasMutated) {
                    childMutated = true;
                    children = updatedChildren;
                }
            }

            if (normalized.parent_id && node.id === normalized.parent_id) {
                const appendedChildren = appendChild(children);
                if (appendedChildren !== children) {
                    childMutated = true;
                    children = appendedChildren;
                }
            }

            if (childMutated) {
                mutated = true;
                return { ...node, children };
            }

            return node;
        });

        return [mutated ? mapped : nodes, mutated];
    };

    const [updated, inserted] = visit(categories);

    if (!inserted) {
        if (!normalized.parent_id) {
            return appendChild(updated);
        }
        return updated;
    }

    return updated;
};
